import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { validateDateAndMinMaxRange } from '../../@utils';
import { CalendarDay, CommonOptions, DateRangeValue, LastUpdatedDateKind, SingleDateValue } from '../@types';

type Params = CommonOptions & (SingleDateValue | DateRangeValue);
export const useCalendar = (params: Params) => {
  const [_value, setValue] = useState<{
    startDate?: Date;
    endDate?: Date;
    lastUpdatedDateType?: LastUpdatedDateKind;
  }>(
    isDateRange(params)
      ? { ...params.value, lastUpdatedDateType: params.initialFocus }
      : { startDate: params.value, endDate: params.value }
  );

  const [displayedDate, setDisplayedDate] = useState(
    dayjs(
      isDateRange(params)
        ? _value.lastUpdatedDateType === 'startDate'
          ? params.value.startDate
          : params.value.endDate
        : params.value
    )
  );

  const calendarDays = getCalendarDays(displayedDate.toDate(), params.minDate, params.maxDate);

  const commonProps = {
    value: _value,
    calendarDays,
    displayedDate,
    setDisplayedDate,
  };

  useEffect(() => {
    // note-@jamie: 외부 입력 값으로 min/maxDate를 벗어나면 calendar에서는 그냥 값을 무시하기로 하고, 입력하는 쪽에서 에러를 표시하는 방향으로.
    if (isDateRange(params)) {
      if (!params.value?.startDate && !params.value?.endDate) {
        // 밖에서 값을 초기화해버리면 calendar에서도 표시된 값을 날린다
        setValue({ startDate: undefined, endDate: undefined, lastUpdatedDateType: 'startDate' });
        return;
      }

      const { isValid: isStartValid, isOutOfRange: isStartOutOfRange } = validateDateAndMinMaxRange({
        date: params.value.startDate,
        minDate: params.minDate,
        maxDate: params.maxDate,
      });
      const { isValid: isEndValid, isOutOfRange: isEndOutOfRange } = validateDateAndMinMaxRange({
        date: params.value.endDate,
        minDate: params.minDate,
        maxDate: params.maxDate,
      });

      if (isStartValid && !isStartOutOfRange && isEndValid && !isEndOutOfRange) {
        const getWhichDateChanged = () => {
          const prevStartDateStr = _value.startDate?.toLocaleDateString();
          const injectedStartDateStr = params.value.startDate?.toLocaleDateString();
          const prevEndDateStr = _value.endDate?.toLocaleDateString();
          const injectedEndDateStr = params.value.endDate?.toLocaleDateString();

          if (prevStartDateStr === injectedStartDateStr && prevEndDateStr === injectedEndDateStr) {
            return _value.lastUpdatedDateType;
          } else if (prevStartDateStr === injectedStartDateStr) {
            return 'endDate';
          } else if (prevEndDateStr === injectedEndDateStr) {
            return 'startDate';
          } else {
            return _value.lastUpdatedDateType;
          }
        };

        setValue({
          startDate: params.value.startDate,
          endDate: params.value.endDate,
          lastUpdatedDateType: getWhichDateChanged(),
        });
      }
    } else {
      if (!params.value) {
        setValue({ startDate: undefined, endDate: undefined, lastUpdatedDateType: 'startDate' });
      } else {
        const { isValid, isOutOfRange } = validateDateAndMinMaxRange({
          date: params.value,
          minDate: params.minDate,
          maxDate: params.maxDate,
        });

        if (isValid && !isOutOfRange) {
          setValue({ startDate: params.value, endDate: params.value, lastUpdatedDateType: 'startDate' });
        }
      }
    }
    // note-@jamie: 의도된 exhaustive-deps -- params.onChange 함수의 참조를 얼려야 해결될듯..
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.value]);

  useEffect(() => {
    // 마지막으로 설정된 날짜와 실제 보이는 캘린더 월을 동기화 한다. (date input으로 입력하는 경우 필요)
    setDisplayedDate(
      dayjs(
        isDateRange(params)
          ? _value.lastUpdatedDateType === 'startDate'
            ? params.value.startDate
            : params.value.endDate
          : params.value
      )
    );

    // note-@jamie: 의도된 exhaustive-deps -- params.onChange 함수의 참조를 얼려야 해결될듯..
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_value.lastUpdatedDateType, params.value]);

  if (isDateRange(params)) {
    return {
      ...commonProps,
      type: 'range' as const,
      onChange: (
        startDate: Date,
        endDate: Date,
        lastUpdatedDateType: LastUpdatedDateKind,
        actionState: 'initial' | 'in-progress' | 'done'
      ) => {
        const { isValid: isStartValid, isOutOfRange: isStartOutOfRange } = validateDateAndMinMaxRange({
          date: startDate,
          minDate: params.minDate,
          maxDate: params.maxDate,
        });
        if (!isStartValid || isStartOutOfRange) return;

        const { isValid: isEndValid, isOutOfRange: isEndOutOfRange } = validateDateAndMinMaxRange({
          date: endDate,
          minDate: params.minDate,
          maxDate: params.maxDate,
        });

        if (!isEndValid || isEndOutOfRange) return;

        if (lastUpdatedDateType === 'startDate') {
          setDisplayedDate(dayjs(startDate));
        } else {
          setDisplayedDate(dayjs(endDate));
        }

        setValue({ startDate, endDate, lastUpdatedDateType });
        params.onChange(startDate, endDate, lastUpdatedDateType, actionState);
      },
    };
  }

  return {
    ...commonProps,
    type: 'single' as const,
    onChange: (date: Date, isDisplayedMonth: boolean) => {
      const { isValid, isOutOfRange } = validateDateAndMinMaxRange({
        date,
        minDate: params.minDate,
        maxDate: params.maxDate,
      });

      if (!isValid || isOutOfRange) return;

      if (!isDisplayedMonth) {
        setDisplayedDate(dayjs(date));
      }

      setValue({ startDate: date, endDate: date, lastUpdatedDateType: 'startDate' });
      params.onChange(date);
    },
  };
};

//#region - util functions dedicated to useCalendar
const isDateRange = (params: SingleDateValue | DateRangeValue): params is DateRangeValue => {
  return 'value' in params && typeof params.value === 'object' && 'startDate' in params.value;
};

const getCalendarDays = (date: Date, minDate?: Date, maxDate?: Date): CalendarDay[] => {
  const currentDate = dayjs(date);
  const firstDayOfMonth = currentDate.startOf('month');
  const lastDayOfMonth = currentDate.endOf('month');

  const isSelectableDate = (d: Date) => {
    const { isValid, isOutOfRange } = validateDateAndMinMaxRange({
      date: d,
      minDate,
      maxDate,
    });

    return isValid && !isOutOfRange;
  };

  // 이전 달의 마지막 날짜들 --- 미래에 이 날짜들로 뭔가 하고 싶을 수도 있어서 일단은 남겨둠
  const firstDayOfWeek = firstDayOfMonth.day();
  const prevMonthDays = Array.from({ length: firstDayOfWeek }, (_, i) => {
    const prevMonthDate = firstDayOfMonth.subtract(firstDayOfWeek - i, 'day').toDate();
    return {
      date: prevMonthDate,
      isDisplayedMonth: false,
      isSelectable: isSelectableDate(prevMonthDate),
      weekIndex: 0,
    };
  });

  // 현재 달의 날짜들
  const currentMonthDays = Array.from({ length: lastDayOfMonth.date() }, (_, i) => {
    const currentMonthDate = firstDayOfMonth.add(i, 'day').toDate();
    return {
      date: currentMonthDate,
      isDisplayedMonth: true,
      isSelectable: isSelectableDate(currentMonthDate),
      weekIndex: Math.floor((firstDayOfWeek + i) / 7),
    };
  });

  // 다음 달의 시작 날짜들 --- 미래에 이 날짜들로 뭔가 하고 싶을 수도 있어서 일단은 남겨둠
  const totalDays = prevMonthDays.length + currentMonthDays.length;
  const remainingDays = 42 - totalDays;

  const nextMonthDays = Array.from({ length: remainingDays }, (_, i) => {
    const nextMonthDate = lastDayOfMonth.add(i + 1, 'day').toDate();
    const weekIndex = Math.floor((firstDayOfWeek + currentMonthDays.length + i) / 7);

    return {
      date: nextMonthDate,
      isDisplayedMonth: false,
      isSelectable: isSelectableDate(nextMonthDate),
      weekIndex,
    };
  });

  return [
    ...(prevMonthDays.length < 7 ? prevMonthDays : []),
    ...currentMonthDays,
    ...(nextMonthDays.length <= 14 ? nextMonthDays : []),
  ];
};
//#endregion
